import { useState, useEffect } from "react";

import { useSearchParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  ReeRPCExtentData,
  ReeRPCResSofaAdminData,
  ReeSofaBestPosition,
  ReeBestPositionsHooksData,
  ReeBestPositionsHook,
} from "../generated/GeneratedTypes";
import {
  BestPositionsCategoriesData,
  ElementsData,
  SofaElement,
} from "./DataTypes";
import { client } from "../RpcClient";
import { config } from "../Config";

enum DimensionType {
  Min = "min",
  Center = "center",
  Max = "max",
  Origin = "origin",
}

enum DimensionIndex {
  X = 0,
  Y = 1,
}
interface Label {
  type: DimensionType;
  index: DimensionIndex;
}

function getHeaders(adminKey: string | null): HeadersInit {
  if (adminKey === null) {
    throw Error();
  }
  return {
    Authorization: `Bearer ${adminKey}`,
    "Content-type": "application/json; charset=UTF-8",
  };
}

function getFactorFromType(type: DimensionType): number {
  let factor = 1;
  if (type === DimensionType.Min) {
    factor = -1;
  } else if (type === DimensionType.Max) {
    factor = 1;
  } else if (type === DimensionType.Center) {
    factor = 0;
  } else {
    throw Error();
  }
  return factor;
}

function labelToName(lab: Label) {
  return (
    (lab.index === DimensionIndex.X ? "X" : "Y") + " " + lab.type.toUpperCase()
  );
}

function formatDim(x: number) {
  return `${x.toFixed(1)}cm`;
}

function extentToCorners(ex: ReeRPCExtentData): string[] {
  if (ex.origin.length !== 3 || ex.extent.length !== 3) {
    return ["-", "-", "-", "-", "-", "-"];
  }
  const min = [];
  const max = [];
  for (let i = 0; i < 2; ++i) {
    min.push(ex.origin[i] - ex.extent[i]);
    max.push(ex.origin[i] + ex.extent[i]);
  }
  return [
    formatDim(min[0]),
    formatDim(ex.origin[0]),
    formatDim(max[0]),
    formatDim(min[1]),
    formatDim(ex.origin[1]),
    formatDim(max[1]),
  ];
}

function BestPositions(props: {
  pointer: number;
  popupClose: () => void;
  elementsData: ElementsData | undefined;
}) {
  const [params] = useSearchParams();
  const [adminData, setAdminData] = useState<ReeRPCResSofaAdminData>();
  const [curBestPositionObj, setCurBestPositionObj] =
    useState<ReeSofaBestPosition>();
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [currentLabel, setCurrentLabel] = useState<string>("");
  const [offset, setOffset] = useState<string>("");
  useEffect(() => {
    const asyncEffect = async () => {
      const data = await client.request("SofaAdminData", {
        pointer: props.pointer,
      });
      setAdminData(data);
      const curBestPosition = data.current_best_position ?? -1;
      if (curBestPosition !== -1) {
        const response = await fetch(
          `${config.apiUrl}/items/sofa_best_positions/${curBestPosition}?fields=*`
        ).then((response) => response.json());
        console.log("SETTING curBestPositionObj to: ", response.data);
        setCurBestPositionObj(response.data);
      }
    };
    asyncEffect();
  }, [props]);
  const isSelectedWidget = (
    <FormControlLabel
      key="is_selected"
      control={
        <Switch
          onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
            await client.request("SofaAdminSetSelected", {
              pointer: props.pointer,
              is_selected: event.target.checked,
            });
            setAdminData(
              await client.request("SofaAdminData", {
                pointer: props.pointer,
              })
            );
          }}
          checked={adminData?.is_selected ?? false}
        />
      }
      label="Is selected"
    />
  );
  const children = [isSelectedWidget];
  const data = adminData;
  if (data !== undefined) {
    const makeLabel = (type: DimensionType, index: DimensionIndex) => {
      return { type, index };
    };
    const labels = [
      makeLabel(DimensionType.Min, DimensionIndex.X),
      makeLabel(DimensionType.Center, DimensionIndex.X),
      makeLabel(DimensionType.Max, DimensionIndex.X),
      makeLabel(DimensionType.Min, DimensionIndex.Y),
      makeLabel(DimensionType.Center, DimensionIndex.Y),
      makeLabel(DimensionType.Max, DimensionIndex.Y),
    ];
    const selectedBounds = extentToCorners(data.selected_element);
    const currentBounds = extentToCorners(data.current_element);
    const rows = [];
    for (let i = 0; i < selectedBounds.length; ++i) {
      const lab = labels[i];
      rows.push({
        name: labelToName(lab),
        sel: selectedBounds[i],
        cur: currentBounds[i],
      });
    }
    const inputs = (
      <TableContainer key="main_table" component={Paper}>
        <Table size="small" aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="right"></TableCell>
              <TableCell align="right">Selected</TableCell>
              <TableCell align="right">Current</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.name}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                <TableCell align="right">{row.sel}</TableCell>
                <TableCell align="right">{row.cur}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
    children.push(inputs);
    const input1 = (
      <Box key="form" sx={{ marginTop: "20px" }}>
        <FormControl sx={{ width: "100px" }}>
          <InputLabel id="current-select-label">Current</InputLabel>
          <Select
            value={currentLabel}
            onChange={(event) => setCurrentLabel(event.target.value)}
          >
            {labels.map((label) => {
              const labelName = labelToName(label);
              return (
                <MenuItem
                  key={`mi-cur-${labelName}`}
                  value={JSON.stringify(label)}
                >
                  {labelName}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Typography sx={{ display: "inline-block", margin: "10px" }}>
          =
        </Typography>
        <FormControl sx={{ width: "100px" }}>
          <InputLabel id="selected-select-label">Selected</InputLabel>
          <Select
            value={selectedLabel}
            onChange={(event) => setSelectedLabel(event.target.value)}
          >
            {labels.map((label) => {
              const labelName = labelToName(label);
              return (
                <MenuItem
                  key={`mi-sel-${labelName}`}
                  value={JSON.stringify(label)}
                >
                  {labelName}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Typography sx={{ display: "inline-block", margin: "10px" }}>
          +
        </Typography>
        <TextField
          onChange={(event) => setOffset(event.target.value)}
          value={offset}
          sx={{ width: "100px" }}
          label="Offset"
          variant="outlined"
        />
        <Button
          key="apply_popup"
          sx={{ marginLeft: "20px" }}
          onClick={async () => {
            const cur = JSON.parse(currentLabel);
            const sel = JSON.parse(selectedLabel);
            let selectedValue =
              data.selected_element.origin[sel.index] +
              getFactorFromType(sel.type) *
                data.selected_element.extent[sel.index];
            const new_location = [...data.current_element.actor_origin];
            new_location[cur.index] =
              selectedValue +
              Number(offset) -
              data.current_element.origin[cur.index] +
              data.current_element.actor_origin[cur.index] -
              getFactorFromType(cur.type) *
                data.current_element.extent[cur.index];
            setAdminData(
              await client.request("SofaAdminSetLocation", {
                new_location,
                pointer: props.pointer,
              })
            );
          }}
        >
          Apply
        </Button>
      </Box>
    );
    children.push(input1);
    const curBestPosition = adminData?.current_best_position ?? -1;
    console.log("CUR BEST POSITION", curBestPosition, adminData);
    if (curBestPosition === -1 || curBestPositionObj === undefined) {
      children.push(
        <Button
          key="mark_best_position"
          onClick={async () => {
            const adminKey = params.get("adminKey");
            if (adminKey === null) {
              throw Error();
            }
            await client.request("SofaAdminMarkAsBestPosition", {
              pointer: props.pointer,
              admin_key: adminKey,
            });
            props.popupClose();
          }}
        >
          Mark this as a best position
        </Button>
      );
    } else {
      const curPosition = curBestPositionObj;
      if (curPosition === undefined) {
        throw Error();
      }
      const firstExternalId =
        props.elementsData?.data.find((it) => it.id === curPosition.first_id)
          ?.external_id ?? "UNDEFINED";
      const secondExternalId =
        props.elementsData?.data.find((it) => it.id === curPosition.second_id)
          ?.external_id ?? "UNDEFINED";
      children.push(
        <Box key="pos_div">
          <Typography
            key="pos_title"
            sx={{ display: "inline-block", margin: "10px" }}
          >
            Current best position (id: {curBestPosition})
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="direction-select-label">
              Best Position Direction
            </InputLabel>
            <Select
              labelId="direction-select-label"
              id="direction-select"
              label="Best Position Direction"
              value={curBestPositionObj?.direction.toString() ?? "0"}
              onChange={async (event) => {
                const adminKey = params.get("adminKey");
                console.log(event.target.value);
                const newDirection = Number(event.target.value);
                await fetch(
                  `${config.apiUrl}/items/sofa_best_positions/${curBestPosition}`,
                  {
                    method: "PATCH",
                    body: JSON.stringify({
                      direction: newDirection,
                    }),
                    headers: getHeaders(adminKey),
                  }
                );
                await client.request("SofaAdminRefreshBestPositions", {});

                setCurBestPositionObj({
                  ...curPosition,
                  direction: newDirection,
                });
              }}
            >
              <MenuItem value={"0"}>Any direction</MenuItem>
              <MenuItem value={"1"}>
                {`${secondExternalId} snaps to ${firstExternalId}`}
              </MenuItem>
              <MenuItem value={"-1"}>
                {`${firstExternalId} snaps to ${secondExternalId}`}
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            key="delete_best_position"
            onClick={async () => {
              const adminKey = params.get("adminKey");
              if (adminKey === null) {
                throw Error();
              }
              await client.request("SofaAdminDeleteBestPosition", {
                pointer: props.pointer,
                admin_key: adminKey,
                best_position_id: curBestPosition,
              });
              props.popupClose();
            }}
          >
            Delete this best position
          </Button>
        </Box>
      );
    }
  }
  return <Box>{children}</Box>;
}

function Hook(props: {
  pointer: number;
  hook: ReeBestPositionsHook;
  categoriesData: BestPositionsCategoriesData;
  updateData: () => void;
  adminData: ReeRPCResSofaAdminData | undefined;
}) {
  const [params] = useSearchParams();
  const [hookLabel, setHookLabel] = useState<string>("");
  const [elementLabel, setElementLabel] = useState<string>("");
  const [offset, setOffset] = useState<string>("");
  const [angle, setAngle] = useState<string>("");
  const [hookShown, setHookShown] = useState(false);
  const [category, setCategory] = useState<number>();
  const hook = props.hook;
  const adminKey = params.get("adminKey");
  return (
    <Box sx={{ borderStyle: "dotted", padding: "5px", marginBottom: "10px" }}>
      <Typography sx={{ flexGrow: 1, flexShrink: 0, marginRight: "5px" }}>
        {hook.id === -1
          ? "NEW HOOOK"
          : `Hook ID ${hook.id} (x: ${hook.x}, y: ${hook.y}, rot: ${
              hook.rotation
            }, cat: ${
              props.categoriesData.data.find((cat) => cat.id === hook.category)
                ?.name ?? ""
            })`}
      </Typography>
      <FormControl sx={{ width: "100%" }}>
        <InputLabel id="category-select-label">Element category</InputLabel>
        <Select
          value={category?.toString() ?? ""}
          onChange={(event) => setCategory(Number(event.target.value))}
        >
          {props.categoriesData.data.map((category) => {
            return (
              <MenuItem
                key={`mi-sel-${category.name}`}
                value={JSON.stringify(category.id)}
              >
                {category.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Box key="form" sx={{ marginTop: "20px" }}>
        <FormControl sx={{ width: "100px" }}>
          <InputLabel id="hook-select-label">Hook</InputLabel>
          <Select
            value={hookLabel}
            onChange={(event) => setHookLabel(event.target.value)}
          >
            {[DimensionIndex.X, DimensionIndex.Y].map((label) => {
              const labelName = label === DimensionIndex.X ? "X" : "Y";
              return (
                <MenuItem key={`mi-cur-${labelName}`} value={label.toString()}>
                  {labelName}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Typography sx={{ display: "inline-block", margin: "10px" }}>
          =
        </Typography>
        <FormControl sx={{ width: "100px" }}>
          <InputLabel id="element-select-label">
            Element{" "}
            {hookLabel.length > 0
              ? Number(hookLabel) === DimensionIndex.X
                ? "X"
                : "Y"
              : ""}
          </InputLabel>
          <Select
            value={elementLabel}
            onChange={(event) => setElementLabel(event.target.value)}
          >
            {[
              DimensionType.Min,
              DimensionType.Max,
              DimensionType.Center,
              DimensionType.Origin,
            ].map((label) => {
              const labelName = label.toUpperCase();
              return (
                <MenuItem key={`mi-sel-${labelName}`} value={label}>
                  {labelName}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Typography sx={{ display: "inline-block", margin: "10px" }}>
          +
        </Typography>
        <TextField
          onChange={(event) => setOffset(event.target.value)}
          value={offset}
          sx={{ width: "100px" }}
          label="Offset"
          variant="outlined"
        />
        <Box sx={{ marginTop: "10px", marginBottom: "10px" }}>
          <TextField
            onChange={(event) => setAngle(event.target.value)}
            value={angle}
            sx={{ width: "100px" }}
            label="Angle"
            variant="outlined"
          />
          <Button
            key="save_popup"
            sx={{ marginLeft: "20px" }}
            onClick={async () => {
              try {
                const adminKey = params.get("adminKey");
                if (adminKey === null) {
                  throw Error();
                }
                const newHookData: Partial<ReeBestPositionsHook> = {
                  element: hook.element,
                };
                if (category !== -1) {
                  newHookData.category = category;
                }
                if (angle !== "") {
                  newHookData.rotation = Number(angle);
                  if (newHookData.rotation < 0 || newHookData.rotation > 360) {
                    throw Error("Angle has to be between 0 and 360 degrees");
                  }
                }
                if (elementLabel !== "" && offset !== "") {
                  const hookDiff = [hook.x, hook.y];
                  const dimensionType = elementLabel as DimensionType;
                  const dimensionIndex = Number(hookLabel);
                  const data = props.adminData;
                  if (data === undefined) {
                    throw Error("No adminData yet");
                  }
                  if (dimensionType === DimensionType.Origin) {
                    hookDiff[dimensionIndex] = Number(offset);
                  } else {
                    const originOffset =
                      data.current_element.origin[dimensionIndex] -
                      data.current_element.actor_origin[dimensionIndex];
                    hookDiff[dimensionIndex] =
                      getFactorFromType(dimensionType) *
                        data.current_element.extent[dimensionIndex] +
                      originOffset +
                      Number(offset);
                  }
                  /*
                  console.log(dimensionIndex);
                  console.log(dimensionType);
                  console.log(hookDiff);
                  console.log(getFactorFromType(dimensionType));
                  console.log(data.current_element.extent[dimensionIndex]);
                  console.log(originOffset);
                  console.log(Number(offset));
                  */
                  if (dimensionIndex === DimensionIndex.X) {
                    newHookData.x = hookDiff[dimensionIndex];
                  } else {
                    newHookData.y = hookDiff[dimensionIndex];
                  }
                }
                if (hook.id === -1) {
                  console.log("Creating new hook");
                  console.log(newHookData);
                  if (
                    newHookData.category === undefined ||
                    newHookData.category === -1
                  ) {
                    throw Error("Category has to be non null for a new hook");
                  }
                  await fetch(`${config.apiUrl}/items/best_positions_hooks`, {
                    method: "POST",
                    body: JSON.stringify(newHookData),
                    headers: getHeaders(adminKey),
                  });
                } else {
                  await fetch(
                    `${config.apiUrl}/items/best_positions_hooks/${hook.id}`,
                    {
                      method: "PATCH",
                      body: JSON.stringify(newHookData),
                      headers: getHeaders(adminKey),
                    }
                  );
                }
                props.updateData();
                await client.request("RefreshHooks", {});
              } catch (err: any) {
                console.error(err);
                alert(
                  `Couldn't save data. Check everything is correct: "${err.message}".`
                );
              }
            }}
          >
            {hook.id === -1 ? "Create new hook" : "Save"}
          </Button>
          {hook.id !== -1 && (
            <Button
              key="delete_popup"
              sx={{ marginLeft: "20px" }}
              onClick={async () => {
                await fetch(
                  `${config.apiUrl}/items/best_positions_hooks/${hook.id}`,
                  {
                    method: "DELETE",
                    headers: getHeaders(adminKey),
                  }
                );
                props.updateData();
              }}
            >
              Delete
            </Button>
          )}
          {hook.id !== -1 && (
            <Button
              key="hide_popup"
              sx={{ marginLeft: "20px" }}
              onClick={async () => {
                const result = await client.request("SwitchShowHooks", {
                  element: props.pointer,
                  hook: hook.id,
                });
                setHookShown(result.show_hooks);
              }}
            >
              {hookShown ? "Hide" : "Show"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function Hooks(props: {
  pointer: number;
  popupClose: () => void;
  elementsData: ElementsData | undefined;
  elementData: SofaElement;
}) {
  const [adminData, setAdminData] = useState<ReeRPCResSofaAdminData>();
  const [data, setData] = useState<ReeBestPositionsHooksData>({ data: [] });
  const [categoriesData, setCategoriesData] =
    useState<BestPositionsCategoriesData>({ data: [] });
  const updateApiData = async (
    setData: (newData: ReeBestPositionsHooksData) => void
  ) => {
    const response: ReeBestPositionsHooksData = await fetch(
      `${config.apiUrl}/items/best_positions_hooks?fields=*`
    ).then((response) => response.json());
    setData(response);
  };
  useEffect(() => {
    updateApiData(setData);
    (async () => {
      const response: BestPositionsCategoriesData = await fetch(
        `${config.apiUrl}/items/best_positions_categories?fields=*`
      ).then((response) => response.json());
      setCategoriesData(response);
    })();
  }, [setData]);
  useEffect(() => {
    const asyncEffect = async () => {
      const data = await client.request("SofaAdminData", {
        pointer: props.pointer,
      });
      setAdminData(data);
    };
    asyncEffect();
  }, [props]);
  const hooks = data.data.filter((it) => it.element === props.elementData.id);
  hooks.push({
    id: -1,
    element: props.elementData.id,
    category: -1,
    state: -1,
    rotation: -1,
    x: 0,
    y: 0,
  });
  return (
    <Box>
      {hooks.map((hook) => {
        return (
          <Hook
            pointer={props.pointer}
            hook={hook}
            categoriesData={categoriesData}
            updateData={() => updateApiData(setData)}
            adminData={adminData}
          />
        );
      })}
    </Box>
  );
}

export default function ElementDetailsAdmin(props: {
  pointer: number;
  popupClose: () => void;
  elementsData: ElementsData | undefined;
  elementData: SofaElement;
}) {
  const [expanded, setExpanded] = useState(0);
  return (
    <Box>
      <Accordion
        expanded={expanded === 1}
        onChange={() => setExpanded(expanded === 1 ? 0 : 1)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={"best-bh-content"}
          id={"best-bh-header"}
        >
          <Typography sx={{ flexGrow: 1, flexShrink: 0, marginRight: "5px" }}>
            Best Positions
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            Set specific connections
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <BestPositions {...props} />
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 2}
        onChange={() => setExpanded(expanded === 2 ? 0 : 2)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={"hooks-bh-content"}
          id={"hooks-bh-header"}
        >
          <Typography sx={{ flexGrow: 1, flexShrink: 0, marginRight: "5px" }}>
            Hooks
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            Set generic hook connections
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Hooks {...props} />
        </AccordionDetails>
      </Accordion>
      <Button key="close_popup" onClick={props.popupClose}>
        Close popup
      </Button>
    </Box>
  );
}
